import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { languages } from "@/i18n";
import { trackPageView } from "@/lib/analytics";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
import PricingPage from "@/pages/PricingPage";
const SharedConversationPage = lazy(() =>
  import("@/pages/SharedConversationPage")
);
const SharedArtifactPage = lazy(() => import("@/pages/SharedArtifactPage"));
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import LegalPage from "@/pages/LegalPage";
import AccountDeletionPage from "@/pages/AccountDeletionPage";
import CookieBanner from "@/components/CookieBanner";
import GlobalLanguageFab from "@/components/GlobalLanguageFab";
import FeedbackWidget from "@/components/FeedbackWidget";
import AccessibilityWidget from "@/components/AccessibilityWidget";
import ExitIntentPopup from "@/components/ExitIntentPopup";
import InstallPWA from "@/components/InstallPWA";
import UseCasesPage from "@/pages/UseCasesPage";
import UseCaseDetailPage from "@/pages/UseCaseDetailPage";
import ModelsPage from "@/pages/ModelsPage";
import ModelPage from "@/pages/ModelPage";
import KitsPage from "@/pages/KitsPage";
import KitPage from "@/pages/KitPage";
const PublicProjectView = lazy(() => import("@/pages/PublicProjectView"));
const PublicResearchPage = lazy(() => import("@/pages/PublicResearchPage"));
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import AboutPage from "@/pages/AboutPage";
import NotFoundPage from "@/pages/NotFoundPage";
import PromptsPage from "@/pages/PromptsPage";
const AffiliatesPage = lazy(() => import("@/pages/AffiliatesPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const ApiDocsPage = lazy(() => import("@/pages/ApiDocsPage"));
import ComparePage from "@/pages/ComparePage";
import "@/App.css";
import { HelmetProvider } from "react-helmet-async";
import { isNative } from "@/utils/capacitor";

// Page tracker — envoie un pageview GA4 à chaque changement de route
const PageTracker = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location]);
  return null;
};

// Shown while a lazily-loaded route chunk downloads. Only the non-prerendered,
// non-first-paint routes are code-split (dashboard, shared/public views, api-
// docs, affiliates, contact) — the prerendered marketing/legal pages stay eager
// so their SSR'd HTML is never replaced by this spinner on hydration.
const RouteFallback = () => (
  <div
    className="min-h-screen flex items-center justify-center bg-background"
    role="status"
    aria-label="Chargement"
  >
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
  </div>
);

// Loading screen shown by Protected/Public route guards while auth context
// initialises (typically 50–300ms during first paint). Single source so both
// guards stay visually consistent. Aria-live=polite for screen readers.
const AuthLoadingScreen = () => (
  <div
    className="min-h-screen bg-background flex items-center justify-center"
    role="status"
    aria-live="polite"
    data-testid="auth-loading"
  >
    <div className="flex items-center gap-3">
      <div
        className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"
        aria-hidden="true"
      />
      <span className="text-muted-foreground">Loading…</span>
    </div>
  </div>
);

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <AuthLoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// Public Route wrapper (redirect if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <AuthLoadingScreen />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
};

// Root "/" route. The Capacitor shell loads server.url = strongerbot.com/ , so
// on the native app a SIGNED-IN user opened onto the MARKETING landing page —
// not their chat — and had to find their way to /dashboard manually. That made
// the app look empty ("no conversations") because the dashboard never mounted.
// On native, send authenticated users straight to the dashboard so the app
// opens into the app. Web is unchanged: "/" always renders the landing there
// (marketing/SEO), signed in or not.
const RootRoute = () => {
  const { user, loading } = useAuth();
  if (isNative) {
    if (loading) return <AuthLoadingScreen />;
    if (user) return <Navigate to="/dashboard" replace />;
  }
  return <LandingPage />;
};

// Per-locale URL wrapper: forces the active language from the URL segment for
// international SEO (/en, /es, /ar…). French stays at the un-prefixed root
// (canonical) — purely additive. Routes are now generated with an explicit
// static locale prefix per page (see LOCALE_PAGES), so this only ever wraps a
// genuine locale; the `!known` guard below is defence-in-depth. It replaced a
// dynamic `/:lng/<page>` that outranked /dashboard/* and shadowed app routes.
const LOCALE_CODES = new Set(languages.map((l) => l.code));
const LocaleRoute = ({ children, lng: lngProp }) => {
  // Routes are generated with a STATIC locale prefix (path="/en/compare"), so
  // there is no `:lng` route param — the locale is passed as a prop. Fall back
  // to useParams for any legacy dynamic-segment caller.
  const params = useParams();
  const lng = lngProp ?? params.lng;
  const { i18n } = useTranslation();
  const location = useLocation();
  const known = LOCALE_CODES.has(lng);
  useEffect(() => {
    if (known && lng !== "fr" && i18n.language !== lng)
      i18n.changeLanguage(lng);
  }, [known, lng, i18n]);
  if (!known) return <Navigate to="/" replace />;
  // French is canonical at the un-prefixed root — /fr/<path> duplicates /<path>.
  // Redirect client-side too (the edge _redirects 301 handles crawlers/hard
  // loads; this covers in-app SPA navigation) so no /fr/* dupe ever renders.
  if (lng === "fr") {
    const stripped = location.pathname.replace(/^\/fr(?=\/|$)/, "") || "/";
    return <Navigate to={stripped + location.search} replace />;
  }
  return children;
};

// Per-locale SEO pages. Generated as an EXPLICIT static prefix per locale
// (e.g. /en/compare) rather than a single dynamic `/:lng/compare`. A dynamic
// `:lng` segment outranks the app's `/dashboard/*` splat route in React
// Router's match scoring, so `/:lng/compare` was shadowing `/dashboard/compare`
// (and `/dashboard/kits`): a signed-in user clicking "Comparer"/"Kits métier"
// matched the locale route with lng="dashboard", failed the locale check, and
// got bounced to the landing page. Static per-locale prefixes can never match a
// non-locale first segment, so the dashboard sub-routes are safe. `fr` is
// generated too — LocaleRoute redirects /fr/* → /* for SPA nav, edge _redirects
// 301s crawlers. Keep this list in sync with the per-locale sitemap generator.
const LOCALE_PAGES = [
  { seg: "", El: LandingPage },
  { seg: "pricing", El: PricingPage },
  { seg: "about", El: AboutPage },
  { seg: "use-cases", El: UseCasesPage },
  { seg: "blog", El: BlogPage },
  { seg: "blog/:slug", El: BlogPostPage },
  { seg: "compare", El: ComparePage },
  { seg: "prompts", El: PromptsPage },
  { seg: "kits", El: KitsPage },
  { seg: "kits/:slug", El: KitPage },
  { seg: "models", El: ModelsPage },
  { seg: "models/:slug", El: ModelPage },
];

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="min-h-screen bg-background">
            <PageTracker />
            {/* Noise overlay for premium feel */}
            <div className="noise-overlay" />

            <Suspense fallback={<RouteFallback />}>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<RootRoute />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/compare" element={<ComparePage />} />
                <Route path="/api-docs" element={<ApiDocsPage />} />
                <Route
                  path="/share/:publicId"
                  element={<SharedConversationPage />}
                />
                <Route path="/artifact/:id" element={<SharedArtifactPage />} />
                <Route
                  path="/login"
                  element={
                    <PublicRoute>
                      <LoginPage />
                    </PublicRoute>
                  }
                />
                <Route
                  path="/register"
                  element={
                    <PublicRoute>
                      <RegisterPage />
                    </PublicRoute>
                  }
                />

                {/* Protected routes */}
                <Route
                  path="/dashboard/*"
                  element={
                    <ProtectedRoute>
                      <DashboardPage />
                    </ProtectedRoute>
                  }
                />

                {/* SEO content routes */}
                <Route path="/models" element={<ModelsPage />} />
                <Route path="/models/:slug" element={<ModelPage />} />
                <Route path="/kits" element={<KitsPage />} />
                <Route path="/kits/:slug" element={<KitPage />} />
                <Route path="/use-cases" element={<UseCasesPage />} />
                <Route
                  path="/use-cases/:slug"
                  element={<UseCaseDetailPage />}
                />
                {/* Phase 7.1 P1 — public read-only project share. Token in
                  URL gates a strict-allowlist projection from the
                  backend (no auth dependency). Page sets robots=noindex
                  via its own Helmet — shares are personal links, not
                  browsable content. */}
                <Route path="/p/:token" element={<PublicProjectView />} />
                {/* Phase 8.3 P0 — Public Research Page viewer. Indexable
                  (no robots noindex) — Perplexity Pages-style SEO viral
                  loop. Token in URL is the gate; backend returns a
                  strict-allowlist projection. */}
                <Route
                  path="/research/:token"
                  element={<PublicResearchPage />}
                />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/prompts" element={<PromptsPage />} />
                <Route path="/affiliates" element={<AffiliatesPage />} />
                <Route path="/contact" element={<ContactPage />} />

                {/* Legal routes */}
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route
                  path="/account-deletion"
                  element={<AccountDeletionPage />}
                />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/legal" element={<LegalPage />} />

                {/* Per-locale SEO URLs — generated as STATIC per-locale prefixes
                  (see LOCALE_PAGES) so a dynamic `/:lng/<page>` can never shadow
                  app splat routes like /dashboard/compare. Additive — French
                  stays canonical at the un-prefixed root (LocaleRoute redirects
                  /fr/*). */}
                {languages.flatMap((l) =>
                  LOCALE_PAGES.map(({ seg, El }) => (
                    <Route
                      key={`${l.code}/${seg}`}
                      path={`/${l.code}${seg ? `/${seg}` : ""}`}
                      element={
                        <LocaleRoute lng={l.code}>
                          <El />
                        </LocaleRoute>
                      }
                    />
                  ))
                )}

                {/* Fallback — branded 404 (was a silent redirect to "/") */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>

            <CookieBanner />
            {/* Guarantees a language switcher on every page (renders only where
                no inline selector exists — see GlobalLanguageFab). */}
            <GlobalLanguageFab />
            <FeedbackWidget />
            {/* Accessibility widget — on EVERY page (public + dashboard). */}
            <AccessibilityWidget />
            <ExitIntentPopup />
            <InstallPWA />
            <Toaster richColors position="top-right" />
          </div>
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
